/**
 * GPIO Extender Configurator - Main Application
 * PWA with Web Serial API
 */

import { serial, SerialConnection } from './serial/serial.js';
import i18n, { t } from './utils/i18n.js';
import { notifications } from './ui/notifications.js';
import { PinsPanel } from './ui/pins.js';
import { Terminal } from './ui/terminal.js';
import { WS2812Panel } from './ui/ws2812.js';
import { DHTPanel } from './ui/dht.js';
import { MonitorPanel } from './ui/monitor.js';

class App {
    constructor() {
        this._pinsPanel = null;
        this._terminal = null;
        this._ws2812Panel = null;
        this._dhtPanel = null;
        this._monitorPanel = null;

        this._init();
    }

    async _init() {
        // Check Web Serial support
        if (!SerialConnection.isSupported()) {
            document.getElementById('not-supported-modal')?.classList.remove('hidden');
            return;
        }

        // Initialize theme
        this._initTheme();

        // Initialize language
        this._initLanguage();

        // Initialize UI components
        this._initUI();

        // Bind events
        this._bindEvents();

    }

    _initTheme() {
        const saved = localStorage.getItem('gpio_extender_theme') || 'auto';

        // Set radio button
        const radio = document.querySelector(`input[name="theme"][value="${saved}"]`);
        if (radio) radio.checked = true;

        // Apply theme
        this._applyTheme(saved);

        // Listen for system theme changes
        this._mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        this._mediaQuery.addEventListener('change', () => {
            const current = localStorage.getItem('gpio_extender_theme') || 'auto';
            if (current === 'auto') {
                this._applyTheme('auto');
            }
        });
    }

    _applyTheme(theme) {
        let resolved = theme;
        if (theme === 'auto') {
            resolved = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        document.documentElement.setAttribute('data-theme', resolved);
    }

    _initLanguage() {
        // Set radio button
        const radio = document.querySelector(`input[name="language"][value="${i18n.lang}"]`);
        if (radio) radio.checked = true;

        // Update DOM
        i18n.updateDOM();

        // Listen for changes
        i18n.onChange(() => {
            i18n.updateDOM();
        });
    }

    _initUI() {
        // Initialize components
        this._pinsPanel = new PinsPanel();
        this._terminal = new Terminal();
        this._ws2812Panel = new WS2812Panel(this._pinsPanel);
        this._dhtPanel = new DHTPanel(this._pinsPanel);
        this._monitorPanel = new MonitorPanel(this._pinsPanel);
    }

    _bindEvents() {
        // Connect button
        document.getElementById('btn-connect')?.addEventListener('click', () => {
            this._connect();
        });

        // Disconnect button
        document.getElementById('btn-disconnect')?.addEventListener('click', () => {
            this._disconnect();
        });

        // Settings button
        document.getElementById('btn-settings')?.addEventListener('click', () => {
            this._showSettings();
        });

        // Settings modal close
        document.querySelector('#settings-modal .modal-close')?.addEventListener('click', () => {
            this._hideSettings();
        });

        document.querySelector('#settings-modal .modal-backdrop')?.addEventListener('click', () => {
            this._hideSettings();
        });

        // Theme change
        document.querySelectorAll('input[name="theme"]').forEach(radio => {
            radio.addEventListener('change', (e) => {
                const theme = e.target.value;
                localStorage.setItem('gpio_extender_theme', theme);
                this._applyTheme(theme);
            });
        });

        // Language change
        document.querySelectorAll('input[name="language"]').forEach(radio => {
            radio.addEventListener('change', (e) => {
                i18n.lang = e.target.value;
            });
        });

        // Tab switching
        document.querySelectorAll('.tab').forEach(tab => {
            tab.addEventListener('click', (e) => {
                this._switchTab(e.target.dataset.tab);
            });
        });

        // Serial events
        serial.on('connected', (info) => {
            this._onConnected(info);
        });

        serial.on('disconnected', () => {
            this._onDisconnected();
        });

        serial.on('error', (error) => {
            notifications.error(error.message);
        });

        // Keyboard shortcuts
        document.addEventListener('keydown', (e) => {
            // Escape to close modals
            if (e.key === 'Escape') {
                this._hideSettings();
            }
        });
    }

    async _connect() {
        try {
            const connectBtn = document.getElementById('btn-connect');
            connectBtn.disabled = true;
            connectBtn.textContent = t('connecting');

            await serial.connect();
        } catch (error) {
            if (error.message !== 'No port selected') {
                notifications.error(error.message);
            }
            this._onDisconnected();
        }
    }

    async _disconnect() {
        try {
            await serial.disconnect();
        } catch (error) {
            notifications.error(error.message);
        }
    }

    async _onConnected(info) {
        document.getElementById('btn-connect')?.classList.add('hidden');
        document.getElementById('btn-disconnect')?.classList.remove('hidden');

        const deviceInfo = document.getElementById('device-info');
        if (deviceInfo) {
            deviceInfo.textContent = info || '';
        }

        notifications.success(t('connected'));

        // Load pin modes and values
        await this._pinsPanel?.loadModes();
        await this._pinsPanel?.loadValues();
    }

    _onDisconnected() {
        const connectBtn = document.getElementById('btn-connect');
        if (connectBtn) {
            connectBtn.classList.remove('hidden');
            connectBtn.disabled = false;
            connectBtn.textContent = t('connect');
        }

        document.getElementById('btn-disconnect')?.classList.add('hidden');

        const deviceInfo = document.getElementById('device-info');
        if (deviceInfo) {
            deviceInfo.textContent = '';
        }

        // Stop background polling when the transport goes away.
        this._monitorPanel?.stop();
        this._dhtPanel?.stop();
    }

    _switchTab(tabId) {
        // Update tab buttons
        document.querySelectorAll('.tab').forEach(tab => {
            tab.classList.toggle('active', tab.dataset.tab === tabId);
        });

        // Update panels
        document.querySelectorAll('.tab-panel').forEach(panel => {
            panel.classList.toggle('active', panel.id === `panel-${tabId}`);
        });

        // Resize monitor canvas when switching to monitor tab
        if (tabId === 'monitor') {
            setTimeout(() => {
                this._monitorPanel?._resizeCanvas();
            }, 100);
        }
    }

    _showSettings() {
        document.getElementById('settings-modal')?.classList.remove('hidden');
    }

    _hideSettings() {
        document.getElementById('settings-modal')?.classList.add('hidden');
    }

}

// Start app when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    window.app = new App();
});
